/**
 * "Welcome back" bar — what the spa's own website says to a visitor it knows.
 *
 * Brooke's deposit hold expired and she booked the same slot again from
 * scratch hours later; the slot was free in between by luck, not by design
 * (Tim, 2026-09-17). A recognised visitor now sees, at the top of the page:
 *   · their next appointment (+ Manage / Book again), or
 *   · an expired hold they can still reclaim (the revive link re-checks the
 *     slot and says so when it has gone), or
 *   · just a way back into booking.
 *
 * Recognition is either a portal session or the RECOGNITION token this device
 * got when it completed a booking (`GET /client-banner/`). "Not you?" throws
 * both away — a shared laptop must be able to stop being Brooke.
 */
export interface BannerAppointment {
  bookingId: number;
  date: string;
  time: string;
  startTime: string;
  serviceName: string;
  staffName: string;
  status: string;
}

export interface BannerData {
  clientId: number;
  firstName: string;
  bookUrl: string;
  portalUrl: string;
  nextAppointment: BannerAppointment | null;
  expiredHold: (BannerAppointment & { reclaimUrl: string }) | null;
}

export interface BannerActions {
  onBook: () => void;
  onPortal: () => void;
  onForget: () => void;
}

const STYLE_ID = 'pp-welcome-styles';
const DISMISS_KEY = 'pp-welcome-dismissed';

const CSS = `
.pp-welcome {
  --wb-accent: var(--pp-accent, #B8623E);
  font: 14px/1.4 var(--font-sans, -apple-system, 'Segoe UI', system-ui, sans-serif);
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  padding: 10px 44px 10px 16px; color: #fff; background: var(--wb-accent);
  box-sizing: border-box; position: relative;
}
.pp-welcome * { box-sizing: border-box; }
.pp-welcome-fixed { position: fixed; top: 0; left: 0; right: 0; z-index: 99990; box-shadow: 0 2px 10px rgba(0,0,0,.18); }
.pp-welcome-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1 1 auto; }
.pp-welcome-title { font-weight: 600; }
.pp-welcome-sub { opacity: .92; font-size: 13px; }
.pp-welcome-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.pp-welcome-btn {
  border: 0; border-radius: 8px; padding: 7px 13px; font: 600 13px/1 inherit; font-family: inherit;
  cursor: pointer; background: #fff; color: var(--wb-accent); white-space: nowrap; text-decoration: none;
}
.pp-welcome-btn-ghost { background: transparent; color: #fff; border: 1.5px solid rgba(255,255,255,.65); }
.pp-welcome-btn:hover { filter: brightness(.96); }
.pp-welcome-notyou { border: 0; background: none; color: #fff; opacity: .8; font: inherit; font-size: 12px; text-decoration: underline; cursor: pointer; padding: 0; align-self: flex-start; }
.pp-welcome-x { position: absolute; top: 6px; right: 8px; border: 0; background: none; color: #fff; font-size: 20px; line-height: 1; cursor: pointer; opacity: .85; padding: 4px 6px; }
.pp-welcome-x:hover { opacity: 1; }
@media (max-width: 560px) { .pp-welcome { padding-right: 38px; } .pp-welcome-actions { width: 100%; } }
`;

function ensureStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

/** "Today", "Tomorrow", else "Fri, Sep 19" — from a YYYY-MM-DD date. */
export function whenLabel(date: string, now: Date = new Date()): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  if (!m) return date || '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function bannerMarkup(data: BannerData, now: Date = new Date()): string {
  const hi = `Welcome back${data.firstName ? `, ${esc(data.firstName)}` : ''}`;
  const appt = data.nextAppointment;
  const hold = data.expiredHold;
  let sub = '';
  let actions = '';
  if (appt) {
    sub = `Your next appointment · ${esc(whenLabel(appt.date, now))} at ${esc(appt.time)}`
      + (appt.serviceName ? ` · ${esc(appt.serviceName)}` : '');
    actions = `<button type="button" class="pp-welcome-btn" data-wb="portal">My appointment</button>
      <button type="button" class="pp-welcome-btn pp-welcome-btn-ghost" data-wb="book">Book another</button>`;
  } else if (hold) {
    // The reclaim link re-checks the slot, so this never promises a time that
    // has already gone.
    sub = `Your ${esc(whenLabel(hold.date, now))} ${esc(hold.time)} appointment was released when the deposit wasn't paid`
      + (hold.serviceName ? ` · ${esc(hold.serviceName)}` : '');
    actions = `<a class="pp-welcome-btn" href="${esc(hold.reclaimUrl)}" data-wb="reclaim">Reclaim it</a>
      <button type="button" class="pp-welcome-btn pp-welcome-btn-ghost" data-wb="book">Book a new time</button>`;
  } else {
    sub = 'You have no upcoming appointments.';
    actions = `<button type="button" class="pp-welcome-btn" data-wb="book">Book now</button>
      <button type="button" class="pp-welcome-btn pp-welcome-btn-ghost" data-wb="portal">My account</button>`;
  }
  return `
    <div class="pp-welcome-text">
      <span class="pp-welcome-title">${hi}</span>
      <span class="pp-welcome-sub">${sub}</span>
      <button type="button" class="pp-welcome-notyou" data-wb="forget">Not you?</button>
    </div>
    <div class="pp-welcome-actions">${actions}</div>
    <button type="button" class="pp-welcome-x" data-wb="close" aria-label="Dismiss">×</button>`;
}

export function dismissedToday(now: number = Date.now()): boolean {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    return !!raw && now - Number(raw) < 86400000;
  } catch { return false; }
}

function markDismissed(): void {
  try { sessionStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
}

let _el: HTMLElement | null = null;
// The host page's own top padding, kept so the bar can give it back — adding
// the bar's height to whatever is currently set compounds on every render.
let _origPadTop: string | null = null;
let _ro: ResizeObserver | null = null;

export function removeBanner(): void {
  if (_ro) { _ro.disconnect(); _ro = null; }
  if (_el) { _el.remove(); _el = null; }
  document.documentElement.style.removeProperty('--pp-welcome-height');
  // _origPadTop stays remembered for the page: re-reading it after we have
  // written our own offset would treat that offset as the site's padding and
  // compound it on the next render.
  if (_origPadTop !== null) document.body.style.paddingTop = _origPadTop;
}

/** Render into `host` (a `<div data-calendoula-welcome>`), else a bar at the top of the page. */
export function showBanner(data: BannerData, actions: BannerActions, host?: HTMLElement | null): void {
  ensureStyles();
  removeBanner();
  const el = document.createElement('div');
  el.className = 'pp-welcome' + (host ? '' : ' pp-welcome-fixed');
  el.setAttribute('role', 'status');
  el.innerHTML = bannerMarkup(data);
  if (host) host.appendChild(el); else document.body.prepend(el);
  _el = el;
  if (!host) {
    // A fixed bar would otherwise sit over the site's own header. Its height
    // is not final at insert time (fonts, wrapping), so follow it instead of
    // measuring once.
    if (_origPadTop === null) _origPadTop = document.body.style.paddingTop;
    const own = parseInt(_origPadTop, 10) || 0;
    const sync = () => {
      if (!_el) return;
      const h = _el.offsetHeight;
      document.documentElement.style.setProperty('--pp-welcome-height', `${h}px`);
      document.body.style.paddingTop = `${h + own}px`;
    };
    sync();
    requestAnimationFrame(sync);
    if (typeof ResizeObserver === 'function') {
      _ro = new ResizeObserver(sync);
      _ro.observe(el);
    }
  }
  el.addEventListener('click', (ev) => {
    const t = (ev.target as HTMLElement).closest<HTMLElement>('[data-wb]');
    if (!t) return;
    const what = t.dataset.wb;
    if (what === 'reclaim') return;            // a real link — let it navigate
    ev.preventDefault();
    if (what === 'book') actions.onBook();
    else if (what === 'portal') actions.onPortal();
    else if (what === 'close') { markDismissed(); removeBanner(); }
    else if (what === 'forget') { markDismissed(); actions.onForget(); removeBanner(); }
  });
}

export async function fetchBanner(apiBase: string, token: string): Promise<BannerData | null> {
  try {
    const res = await fetch(`${apiBase.replace(/\/+$/, '')}/api/v1/client-banner/`, {
      headers: { Authorization: `ClientBearer ${token}` },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}
